import { useState } from 'react';
import { Urls } from '../Urls';
import { useAppContext, ContextConstants } from '../Context/AppContext/AppContext';
import axios from '../Axios';
import { useTransporterData } from './useTransporterData';

/**
 * useCreateTransporterProfile
 *
 * Unified hook for creating a new transporter profile.
 * Used by both OnboardingQuickForm (manual path) and OnboardingReview (AI path).
 *
 * What it does:
 *  1. POSTs the payload to Urls.Transporter.Post
 *  2. Dispatches ContextConstants.SetData to update AppContext immediately
 *     (so all context consumers reflect the new profile without waiting for refetch)
 *
 * @returns {{ createProfile: Function, saving: boolean, error: string }}
 *
 * Usage:
 *   const { createProfile, saving, error } = useCreateTransporterProfile();
 *   const { success } = await createProfile(payload);
 *   if (success) onSuccess(payload);
 */
export const useCreateTransporterProfile = () => {
    const { dispatch } = useAppContext();
    const { refetch } = useTransporterData();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const createProfile = async (payload) => {
        setSaving(true);
        setError('');
        try {
            await axios.post(Urls.Transporter.Post, payload, { skipErrorToast: true });
            refetch()
            return { success: true };
        } catch (e) {
            console.error('[useCreateTransporterProfile] Save error:', e);
            const apiData = e?.response?.data;
            const message = apiData?.message || 'Could not save. Please try again.';
            setError(message);
            return { success: false, message, value: apiData?.value };
        } finally {
            setSaving(false);
        }
    };

    return { createProfile, saving, error };
};
